import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import Calculate from '~/Assets/icons/calculate.svg';
import NumberInput from '~ComponentsImproved/Numpad';
import Modal from '~ComponentsImproved/Modal';
import { useButton } from '~Hooks';

const ContentWrapper = styled.div<{ width?: string }>`
  width: 100%;
  position: relative;
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  max-height: 90vh;

  @media screen and (min-width: 600px) {
    width: ${({ width }) => width || '450px'};
  }
`;

const Title = styled.div`
  color: #fff;
  margin-bottom: 20px;
  font-weight: bold;
  font-size: 30px;
  text-align: center;
  width: 100%;
`;

const Option = styled.div<{ responsive?: boolean; size?: 'sm' | 'md' }>`
  ${({ size, responsive }) => {
    if (responsive) {
      return `
        width: auto;
        height: auto;
        padding: ${size === 'sm' ? '8px 14px' : '10px 16px'};
        font-size: ${size === 'sm' ? '20px' : '24px'};
        line-height: normal;
      `;
    }

    return `
      width: ${size === 'sm' ? '64px' : '70px'};
      height: ${size === 'sm' ? '64px' : '70px'};
      padding: 0;
      font-size: ${size === 'sm' ? '24px' : '30px'};
      line-height: ${size === 'sm' ? '60px' : '70px'};
    `;
  }}

  background: #d5d7d8;
  border-radius: 4px;
  color: #1c232c;
  font-weight: bold;
  margin: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap; // Ensures the text stays in one line

  @media screen and (min-width: 600px) {
    margin: ${({ size }) => (size === 'sm' ? '12px' : '20px')};
  }
`;

const Icon = styled.div`
  height: 50px;
  width: 50px;
  mask-image: url(${Calculate});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #1c232c;
  display: inline-block;
`;

/** We use this as its own component to avoid bubbling */
const OptionButton: FC<{
  opt: number | string;
  responsiveBoxes: boolean;
  size: 'sm' | 'md';
  onSelect: (value: number | string) => void;
}> = ({ opt, responsiveBoxes, size, onSelect }) => {
  const button = useButton({
    onClick: () => onSelect(opt),
  });

  return (
    <Option key={opt} responsive={responsiveBoxes} size={size} {...button}>
      {opt}
    </Option>
  );
};

interface Props {
  enableCustomInput?: boolean;
  responsiveBoxes?: boolean;
  width?: string;
  title: string | React.ReactNode;
  options: number[] | string[];
  onSelect: (value: number | string) => void;
  onCancel?: () => void;
  size?: 'sm' | 'md'; // New optional prop for size
}

/**
 * Pops up a modal with buttons mapped out - for instance used when selecting coverage or beepers
 */
const SelectOptionModal: FC<Props> = ({
  enableCustomInput,
  responsiveBoxes = false,
  width,
  title,
  options,
  onSelect,
  onCancel,
  size = 'md', // Default size is "md"
}) => {
  const [showNumberInput, setShowNumberInput] = useState<boolean>(false);

  return (
    <Modal isShowing={true} hide={onCancel}>
      <ContentWrapper width={width}>
        <Title>{title}</Title>
        {options.map((opt) => (
          <OptionButton
            key={opt}
            opt={opt}
            responsiveBoxes={responsiveBoxes}
            size={size}
            onSelect={onSelect}>
            {opt}
          </OptionButton>
        ))}
        {enableCustomInput && (
          <Option
            responsive={responsiveBoxes}
            size={size}
            onTouchStart={() => setShowNumberInput(true)}>
            <Icon />
          </Option>
        )}
      </ContentWrapper>

      {showNumberInput && (
        <NumberInput
          maxCount={2}
          onClose={() => setShowNumberInput(false)}
          onSubmit={(val: string) => onSelect(parseInt(val))}
        />
      )}
    </Modal>
  );
};

export default SelectOptionModal;
